import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';

/**
 * Hook para hints de primera visita por usuario+pantalla.
 *
 * Iter 313.k.3 (Renzo, feb-2026): además de `localStorage` (cliente), ahora
 * también persiste en el backend `POST /api/user-prefs/hints-seen/{hintId}`
 * para que el "Entendido" NO reaparezca al cambiar de navegador, dispositivo
 * o al limpiarse la caché local.
 *
 * - `hintId` único por página (ej. 'flujo-caja-grid-editable').
 * - Fuentes: localStorage `chirino-hints-seen:<email>` + backend user_hints_seen.
 * - Se cierra con la X o con "Entendido" y queda visto para siempre.
 */
const STORAGE_PREFIX = 'chirino-hints-seen:';

// Cache en memoria de la lista del servidor por email — evita spamear el
// endpoint cuando hay varios FirstTimeHint en la misma pantalla.
const serverHintsCache = new Map(); // email → Promise<Set<hint_id>>

async function getServerHints(email) {
  if (!email) return new Set();
  if (!serverHintsCache.has(email)) {
    const p = api.get('/user-prefs/hints-seen')
      .then((r) => new Set(r?.data?.hint_ids || []))
      .catch(() => new Set());
    serverHintsCache.set(email, p);
    // Auto-invalidar al minuto por si el usuario abre en otro tab
    setTimeout(() => serverHintsCache.delete(email), 60_000);
  }
  return serverHintsCache.get(email);
}

export function useFirstTimeHint(hintId) {
  const { user } = useAuth();
  const userKey = user?.email || 'guest';
  const storageKey = `${STORAGE_PREFIX}${userKey}`;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user?.email || !hintId) return;
    let cancelled = false;

    (async () => {
      // 1) Chequeo local (sync, rápido)
      let seenLocal = [];
      try {
        const raw = localStorage.getItem(storageKey);
        seenLocal = raw ? JSON.parse(raw) : [];
      } catch { seenLocal = []; }
      if (seenLocal.includes(hintId)) return;

      // 2) Chequeo servidor (async) — si el usuario lo descartó en otro
      // navegador/dispositivo, evita mostrarlo de nuevo.
      const serverSet = await getServerHints(user.email);
      if (cancelled) return;
      if (serverSet.has(hintId)) {
        // Sincronizamos localStorage para próximos loads
        try {
          const next = Array.from(new Set([...seenLocal, hintId]));
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch { /* ignore */ }
        return;
      }

      // Ninguna fuente lo tiene → mostramos con delay para que la página
      // termine de renderizar.
      const t = setTimeout(() => { if (!cancelled) setVisible(true); }, 600);
      return () => clearTimeout(t);
    })();

    return () => { cancelled = true; };
  }, [user?.email, hintId, storageKey]);

  const dismiss = useCallback(() => {
    setVisible(false);
    // Persistir local (sync)
    try {
      const raw = localStorage.getItem(storageKey);
      const seen = raw ? JSON.parse(raw) : [];
      if (!seen.includes(hintId)) {
        seen.push(hintId);
        localStorage.setItem(storageKey, JSON.stringify(seen));
      }
    } catch { /* ignore */ }
    // Persistir servidor (best-effort, no bloquea UI si falla)
    if (user?.email) {
      api.post(`/user-prefs/hints-seen/${encodeURIComponent(hintId)}`)
        .then(() => {
          // Actualizar el cache en memoria
          const cached = serverHintsCache.get(user.email);
          if (cached) cached.then((set) => set.add(hintId));
        })
        .catch(() => { /* ignore — sigue guardado localmente */ });
    }
  }, [hintId, storageKey, user?.email]);

  return { visible, dismiss };
}
